import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useQueryClient } from "@tanstack/react-query";
import { camelCase } from "lodash";
import { CamelCasedProperties } from "type-fest";
import { VITE_UTILITIES_BASE_URL } from "../../../env";
import { Utility } from "../../../types";
import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
} from "../../postgrestApi";
import request from "../../request";
import FetchClients, { ClientOutput } from "../client";
import { throwBackpackSuperagentResponseError } from "../errors";
import { UTILITY_BILL_CONSUMPTION_KEY } from "./consumption";

// INNO-765: can use generated types with no modifications once done
export type UtilityBillBase = Omit<
  CamelCasedProperties<ClientOutput["utilities"]["Bill"]>,
  "utilityType" | "taxesAndOther" | "totalCost"
> & {
  utilityType: Utility;
  taxesAndOther?: number | null;
  totalCost?: number | null;
};

export type UtilityBillAggregatedConsumption = Omit<
  CamelCasedProperties<ClientOutput["utilities"]["BillAggregatedConsumption"]>,
  | "utilityType"
  | "taxesAndOther"
  | "totalCost"
  | "totalConsumptionCost"
  | "totalConsumption"
  | "blendedRate"
> & {
  utilityType: Utility;
  taxesAndOther?: number | null;
  totalCost?: number | null;
  totalConsumptionCost?: number | null;
  totalConsumption: number;
  blendedRate: number;
};

export type UnitParam = ClientOutput["utilities"]["UnitConversion"];

const bill = {
  useQueryAllAggregatedConsumption,
  useQueryAggregatedConsumption,
  useQueryYears,
  mutations: {
    usePost,
    usePut,
    useDelete,
  },
} as const;

export default bill;

// GET

// endpoint requires {feature: "Utilities", access: "read"}
const getAllUtilityBillsAggregatedConsumption = (
  siteId: string | number,
  unit: UnitParam,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<UtilityBillAggregatedConsumption[]> =>
  request
    .get(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/bills`)
    .set(checkFeatureAccessHeader)
    .query({ unit })
    .then(({ body }) =>
      recursiveCamelCaseCipher(body).map(
        (res: UtilityBillAggregatedConsumption) => ({
          ...res,
          utilityType: camelCase(res.utilityType),
        })
      )
    );

function useQueryAllAggregatedConsumption(
  siteId: number,
  options: { unit: UnitParam } = { unit: "default" }
) {
  const { unit } = options;
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getAllUtilityBillsAggregatedConsumption(
        siteId,
        unit,
        checkFeatureAccessHeader
      ),
    {
      queryKey: [UTILITY_BILL_CONSUMPTION_KEY, siteId, "aggregated", unit],
      select: (items): UtilityBillAggregatedConsumption[] =>
        items.map((item) =>
          item.utilityType === "wasteWater"
            ? { ...item, utilityType: "water" }
            : item
        ),
    }
  );
}

// endpoint requires {feature: "Utilities", access: "read"}
const getUtilityBillAggregatedConsumption = (
  siteId: string | number,
  billId: number,
  unit: UnitParam,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<UtilityBillAggregatedConsumption> =>
  request
    .get(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/bills/${billId}`)
    .set(checkFeatureAccessHeader)
    .query({ unit })
    .then(({ body }) => ({
      ...recursiveCamelCaseCipher(body),
      utilityType: camelCase(body.utility_type),
    }));

function useQueryAggregatedConsumption(
  siteId: number,
  billId: number,
  options: { unit?: UnitParam; enabled?: boolean } = {}
) {
  const { unit = "default", enabled } = options;
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getUtilityBillAggregatedConsumption(
        siteId,
        billId,
        unit,
        checkFeatureAccessHeader
      ),
    {
      queryKey: [
        UTILITY_BILL_CONSUMPTION_KEY,
        siteId,
        billId,
        "aggregated",
        unit,
      ],
      select: (item): UtilityBillAggregatedConsumption =>
        item.utilityType === "wasteWater"
          ? { ...item, utilityType: "water" }
          : item,
      enabled,
    }
  );
}

// POST

type PostUtilityBillRequest = Partial<
  Omit<UtilityBillAggregatedConsumption, "id">
>;

type UtilityBillMutationResponse =
  ClientOutput["utilities"]["CreateBillResponse"];

const generateErrorMessage = (
  { accountNumber, providerId }: PostUtilityBillRequest,
  siteId: number
) => {
  if (accountNumber && providerId) {
    return `Unable to create bill, make sure utility account "${accountNumber}" and provider ID "${providerId}" are set up for site ${siteId}.`;
  }
  if (accountNumber) {
    return `Unable to create bill, make sure utility account "${accountNumber}" is set up for site ${siteId}.`;
  }
  if (providerId) {
    return `Unable to create bill, make sure provider ID "${providerId}" is set up for site ${siteId}.`;
  }
  return "Unable to create bill.";
};

// endpoint requires {feature: "Utilities", access: "edit"}
const postUtilityBill = (
  siteId: number,
  req: PostUtilityBillRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<UtilityBillMutationResponse> =>
  request
    .post(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/bills`)
    .set("Content-Type", "application/json")
    .set(checkFeatureAccessHeader)
    .send(recursiveSnakeCaseCipher(req, { convertUtilityValues: true }))
    .then((res) => {
      if (res.status !== 200) {
        throw new Error(`Unable to create bill for site: ${siteId}`);
      }
      return recursiveCamelCaseCipher(res.body);
    })
    .catch((e) => {
      if (e.response?.statusCode === 404) {
        throw new Error(generateErrorMessage(req, siteId));
      }
      throwBackpackSuperagentResponseError(e);
    });

function usePost(siteId: number) {
  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (req: PostUtilityBillRequest, checkFeatureAccessHeader) =>
      postUtilityBill(siteId, req, checkFeatureAccessHeader)
  );
}

// PUT

type PutUtilityBillRequest = Partial<UtilityBillBase> & { id: number };

// endpoint requires {feature: "Utilities", access: "edit"}
const putUtilityBill = (
  siteId: number,
  req: PutUtilityBillRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<UtilityBillMutationResponse> =>
  request
    .put(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/bills/${req.id}`)
    .set("Content-Type", "application/json")
    .set(checkFeatureAccessHeader)
    .send(recursiveSnakeCaseCipher(req, { convertUtilityValues: true }))
    .then((res) => {
      if (res.status !== 200) {
        throw new Error(`Unable to update bill for site: ${siteId}`);
      }
      return recursiveCamelCaseCipher(res.body);
    })
    .catch(throwBackpackSuperagentResponseError);

function usePut(siteId: number) {
  const queryClient = useQueryClient();
  const queryKey = [UTILITY_BILL_CONSUMPTION_KEY, siteId];

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (req: PutUtilityBillRequest, checkFeatureAccessHeader) =>
      putUtilityBill(siteId, req, checkFeatureAccessHeader),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey,
        });
      },
    }
  );
}

// DELETE

// endpoint requires {feature: "Utilities", access: "edit"}
const deleteUtilityBill = (
  siteId: number,
  billId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<void> =>
  request
    .delete(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/bills/${billId}`)
    .set(checkFeatureAccessHeader)
    .then(({ status }) => {
      if (status !== 200 && status !== 204) {
        // unsure which status yet
        // Backend should return error response status and message for these cases
        throw new Error("Delete bill failed.");
      }
    })
    .catch(throwBackpackSuperagentResponseError);

function useDelete(siteId: number, billId: number) {
  const queryClient = useQueryClient();
  const queryKey = [UTILITY_BILL_CONSUMPTION_KEY, siteId];
  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (_: void, checkFeatureAccessHeader) =>
      deleteUtilityBill(siteId, billId, checkFeatureAccessHeader),
    {
      onMutate: async () => {
        // cancel queries
        await queryClient.cancelQueries({
          queryKey,
        });
      },
      onSuccess: async () => {
        // we just need to remove the aggregated consumption record
        // synchronously as this removes the single consumption records
        queryClient.setQueryData(
          [UTILITY_BILL_CONSUMPTION_KEY, siteId, "aggregated"],
          (old?: UtilityBillAggregatedConsumption[]) =>
            (old ?? []).filter((agg) => agg.id !== billId)
        );

        await queryClient.invalidateQueries({
          queryKey,
        });
      },
    }
  );
}

const UTILITY_BILL_YEARS_KEY = ["utilityBillYears"];

// endpoint requires {feature: "Utilities", access: "read"}
async function getUtilityBillYears(
  accountId: number | undefined,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.GET("/bills/years", {
    params: {
      query: {
        account_id: accountId,
      },
    },
    headers: checkFeatureAccessHeader,
  });
  if (data) return data;
  throw new Error(error);
}

function useQueryYears(accountId?: number) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getUtilityBillYears(accountId, checkFeatureAccessHeader),
    {
      queryKey: [...UTILITY_BILL_YEARS_KEY, accountId],
    }
  );
}
